import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@atlas/ui/web";
import { GerenciarDoisFA } from "../../components/GerenciarDoisFA";
import { TrocarMinhaSenha } from "../../components/TrocarMinhaSenha";
import { atlas } from "../../lib/sdk";

/** Minha conta — operador da prefeitura. */
export function PrefeituraConta() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 720 }}>
      <header>
        <span className="eyebrow">Prefeitura</span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Minha conta</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 6 }}>
          Gerencie a segurança do login e as exigências que sua prefeitura impõe aos servidores e consignatárias parceiras.
        </p>
      </header>
      <TrocarMinhaSenha trocar={(atual, nova) => atlas.prefeitura.trocarMinhaSenha(atual, nova)} />
      <GerenciarDoisFA />
      <RegrasDePropostas />
    </div>
  );
}

/** Configuração de exigências de senha aplicadas aos servidores/bancos.
 *  Cliente pediu 30/07/2026: a prefeitura decide se o servidor precisa
 *  autenticar por senha em cada operação sensível. */
function RegrasDePropostas() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["prefeitura", "config"], queryFn: () => atlas.prefeitura.getConfig() });
  const [msg, setMsg] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: { exigeSenhaAverbacao?: boolean; exigeSenhaProposta?: boolean; exigeCcb?: boolean; exigeBanco2FA?: boolean }) =>
      atlas.prefeitura.setConfig(body),
    onSuccess: (r) => {
      qc.setQueryData(["prefeitura", "config"], r);
      setMsg("Regras salvas.");
      setTimeout(() => setMsg(null), 3000);
    },
    onError: (err) => setMsg((err as Error).message || "Erro ao salvar"),
  });

  if (q.isLoading) return <Card>Carregando regras…</Card>;
  if (q.error || !q.data) return <Card>Erro ao carregar regras.</Card>;

  const c = q.data;
  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Regras de propostas e averbação
        </span>
        <h3 style={{ margin: 0 }}>Segurança das operações do servidor</h3>
        <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13, lineHeight: 1.5 }}>
          Ative estas exigências pra reforçar o consentimento do servidor. Nenhuma delas altera a análise
          de crédito da consignatária — todas atuam antes da operação virar contrato.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 18 }}>
        <ToggleLinha
          label="Servidor precisa digitar senha ao enviar uma proposta"
          descricao="Quando o servidor clicar 'Aceito e autorizo' no termo, o Atlas pede a senha dele. Sem ela, a proposta não é registrada."
          checked={c.exigeSenhaProposta}
          onChange={(v) => save.mutate({ exigeSenhaProposta: v })}
          disabled={save.isPending}
        />
        <ToggleLinha
          label="Consignatária precisa da senha do servidor ao averbar direto pela ficha"
          descricao="Fluxo 'empréstimo iniciado pela consignatária' na tela Margem/Contratação. Sem a senha do servidor, a consignatária não consegue averbar sem passar por uma proposta que o servidor enviou."
          checked={c.exigeSenhaAverbacao}
          onChange={(v) => save.mutate({ exigeSenhaAverbacao: v })}
          disabled={save.isPending}
        />
        <ToggleLinha
          label="Consignatária precisa passar por 2FA ao averbar"
          descricao="Se ativado, a consignatária só consegue averbar após uma verificação em duas etapas do operador."
          checked={c.exigeBanco2FA}
          onChange={(v) => save.mutate({ exigeBanco2FA: v })}
          disabled={save.isPending}
        />
      </div>

      {msg ? (
        <div style={{ marginTop: 14, fontSize: 13, color: msg.startsWith("Erro") ? "var(--danger-500)" : "var(--emerald-500)" }}>
          {msg}
        </div>
      ) : null}
    </Card>
  );
}

function ToggleLinha({
  label, descricao, checked, onChange, disabled,
}: {
  label: string; descricao: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        style={{ marginTop: 3 }}
      />
      <span>
        <b style={{ color: "var(--text)" }}>{label}</b>
        <br />
        <span style={{ color: "var(--text-muted)", fontSize: 13, lineHeight: 1.5 }}>{descricao}</span>
      </span>
    </label>
  );
}
